/**
 * Where every individual setting lives, as data (T16). The registry itself; its
 * shape and the `anchor()` builder are the leaf beside it
 * (`setting-anchors.types.ts`), split out at the ~300-line ceiling.
 */

import { ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID } from '@syncanix/shared';

import { ENGAGEMENT_PATH } from '@/features/engagement/engagement-tabs';
import { EVERY_ROLE_SURFACE } from '@/lib/dashboard-permissions';

import { paneParamFor } from './setting-anchor-routes';
import { CONNECTIONS_SETTING_ANCHORS } from './setting-anchors.connections';
import { DOCS_BILLING_SETTING_ANCHORS } from './setting-anchors.docs-billing';
import { LIMITS_SETTING_ANCHORS } from './setting-anchors.limits';
import { STUDIO_SETTING_ANCHORS } from './setting-anchors.studio';
import { STUDIO_GLOBAL_SETTING_ANCHORS } from './setting-anchors.studio-global';
import { TEAM_SETTING_ANCHORS } from './setting-anchors.team';
import { anchor } from './setting-anchors.types';
import { WORKSPACE_SETTING_ANCHORS } from './setting-anchors.workspace';
import { ACCOUNT_PREFERENCE_CONTROLS } from './setting-controls';

import type { SettingAnchor } from './setting-anchors.types';

export type { SettingAnchor } from './setting-anchors.types';

/**
 * Every addressable setting, keyed by the same slug its card uses to register
 * with the unsaved-changes registry where it has one — so the two identities
 * cannot drift apart.
 */
/** The user-learning settings route, named once because five anchors point at it. */
const USERS_SETTINGS = '/users/settings';

export const SETTING_ANCHORS = {
  ...WORKSPACE_SETTING_ANCHORS,

  // Widget Design Studio — Global settings step (T13): the cards, as a sibling map.
  ...STUDIO_GLOBAL_SETTING_ANCHORS,

  // `/engagement` — Where it appears (T14). `/page-rules` merged into this tab,
  // so both anchors moved with the cards. `chat-availability` addresses the
  // tab's STATE card rather than the fuller availability card beneath it: that
  // one lives inside `AdvancedSection` and renders nothing in Simple mode, and
  // this registry's contract is that a jump never lands on a card the caller
  // cannot see.
  'chat-availability': anchor(
    'setting-chat-availability',
    ENGAGEMENT_PATH,
    'workspacesettings.chatAvailability.heading',
    'surfaces',
    'edit',
    { keywordsKey: 'settingSearch.chatAvailability', pane: 'placement' },
  ),
  'host-actions': anchor(
    'setting-host-actions',
    ENGAGEMENT_PATH,
    'workspacesettings.hostActions.heading',
    'surfaces',
    'edit',
    { keywordsKey: 'settingSearch.hostActions', pane: 'placement' },
  ),

  // `/experience` (Adaptive Compositions D4) — the welcome control. The kill
  // switch on the same page is the Labs entry and is anchored there, not here:
  // one setting, one anchor, or a jump lands on whichever copy was registered
  // last.
  'chat-experience': anchor(
    'setting-chat-experience',
    '/experience',
    'chatexperience.welcome.heading',
    'settings',
    'admin',
    {
      keywordsKey: 'settingSearch.chatExperience',
      // The page itself is Labs-gated, so the setting is too — see the
      // `labsFlag` note on `SettingAnchor`.
      labsFlag: ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID,
    },
  ),

  // `/experience` (Adaptive Compositions D6b) — the style rules. Its OWN anchor
  // and its OWN audit key rather than sharing the welcome card's: they are two
  // cards, two mutations and two action types, so one anchor would land a ⌘K jump
  // on whichever card was registered last.
  'chat-experience-rules': anchor(
    'setting-chat-experience-rules',
    '/experience',
    'chatexperience.rules.heading',
    'settings',
    'admin',
    {
      keywordsKey: 'settingSearch.chatExperienceRules',
      // The page itself is Labs-gated, so the setting is too — see the
      // `labsFlag` note on `SettingAnchor`.
      labsFlag: ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID,
    },
  ),

  // `/experience` (Adaptive Compositions D5b) — the hints builder.
  'chat-experience-hints': anchor(
    'setting-chat-experience-hints',
    '/experience',
    'chatexperience.hints.heading',
    'settings',
    'admin',
    {
      keywordsKey: 'settingSearch.chatExperienceHints',
      // The page itself is Labs-gated, so the setting is too — see the
      // `labsFlag` note on `SettingAnchor`.
      labsFlag: ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID,
    },
  ),

  // `/engagement` — Nudges (T15). `/proactive` merged into this tab; the id is
  // rendered by the tab's state card, which carries the master switch the
  // setting names and is present in both UI modes.
  // `/account` — Preferences (SYN-148). The one anchored card that mixes
  // UNRELATED options, so it also registers each of them as a control: an
  // operator who types "language" lands on the language group, not on a card
  // where it is the third of four.
  //
  // `EVERY_ROLE_SURFACE` because these are the acting user's own presentation
  // preferences. They belong to whoever is signed in and to no workspace grant
  // at all, so gating them on a surface would hide a working control from a
  // viewer — the D-9 failure, one surface along.
  'account-preferences': anchor(
    'setting-account-preferences',
    '/account',
    'account.preferencesHeading',
    EVERY_ROLE_SURFACE,
    'view',
    {
      keywordsKey: 'settingSearch.accountPreferences',
      controls: ACCOUNT_PREFERENCE_CONTROLS,
    },
  ),

  // `/experience` (Adaptive Compositions) — the two READ cards on the same page.
  // Both are `settings:view` rather than `admin`: they show what the workspace
  // is spending and how the model is choosing, and neither writes anything.
  //
  // The Labs KILL SWITCH on this page is deliberately still absent. It is the
  // Labs entry, anchored on `/settings/experimental`, and the registry's rule is
  // one setting, one anchor — a second copy would land a jump on whichever was
  // registered last.
  'chat-experience-budget': anchor(
    'setting-chat-experience-budget',
    '/experience',
    'chatexperience.budget.heading',
    'settings',
    'view',
    {
      keywordsKey: 'settingSearch.chatExperienceBudget',
      labsFlag: ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID,
    },
  ),
  'chat-experience-affinity': anchor(
    'setting-chat-experience-affinity',
    '/experience',
    'chatexperience.affinity.heading',
    'settings',
    'view',
    {
      keywordsKey: 'settingSearch.chatExperienceAffinity',
      labsFlag: ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID,
    },
  ),

  // `/users/settings` — the user-learning controls (UGL-105 A0). The four that
  // WRITE are `catalog:edit`, which is what the server gates them on
  // (`frontier.resolver.ts` for the master switch,
  // `user-learning-settings.resolver.ts` for the budget and the config,
  // `claim-capture.resolver.ts` for the allowlist) — not `analytics`, which is
  // the surface the PAGE sits on and would have offered every one of them to a
  // role the server refuses.
  'user-learning': anchor(
    'setting-user-learning',
    USERS_SETTINGS,
    'userlearning.master.heading',
    'catalog',
    'edit',
    { keywordsKey: 'settingSearch.userLearning' },
  ),
  'user-learning-budget': anchor(
    'setting-user-learning-budget',
    USERS_SETTINGS,
    'userlearning.budget.heading',
    'catalog',
    'edit',
    { keywordsKey: 'settingSearch.userLearningBudget' },
  ),
  'user-learning-config': anchor(
    'setting-user-learning-config',
    USERS_SETTINGS,
    'userlearning.config.heading',
    'catalog',
    'edit',
    { keywordsKey: 'settingSearch.userLearningConfig' },
  ),
  'claim-capture': anchor(
    'setting-claim-capture',
    USERS_SETTINGS,
    'users.claimCapture.heading',
    'catalog',
    'edit',
    { keywordsKey: 'settingSearch.claimCapture' },
  ),
  // A read-only log, so `analytics:view` — the surface its own query is gated on.
  'identify-activity': anchor(
    'setting-identify-activity',
    USERS_SETTINGS,
    'users.identifyLog.heading',
    'analytics',
    'view',
    { keywordsKey: 'settingSearch.identifyActivity' },
  ),

  ...LIMITS_SETTING_ANCHORS,
  ...DOCS_BILLING_SETTING_ANCHORS,
  ...CONNECTIONS_SETTING_ANCHORS,
  ...TEAM_SETTING_ANCHORS,
  ...STUDIO_SETTING_ANCHORS,

  proactive: anchor(
    'setting-proactive',
    ENGAGEMENT_PATH,
    'workspacesettings.proactive.heading',
    'surfaces',
    'edit',
    { keywordsKey: 'settingSearch.proactive', pane: 'nudges' },
  ),
} as const satisfies Record<string, SettingAnchor>;

/** Every setting key, in the order the palette should offer them. */
export const SETTING_KEYS = Object.keys(SETTING_ANCHORS) as SettingKey[];

/**
 * Narrowed to the STRING keys. `keyof` over an object built by spreading a
 * sibling map widens to `string | number | symbol`, which is not what any
 * consumer means by "a setting key" and made every caller that passes one to a
 * string parameter a type error.
 */
export type SettingKey = Extract<keyof typeof SETTING_ANCHORS, string>;

/**
 * The in-app link that opens a setting: its route, its tab, and the DOM id of
 * the card — or of one control inside it — as the hash.
 *
 * **The hash does not do the scrolling, and this comment used to say it did.**
 * React Router performs a client-side navigation; nothing in the browser reacts
 * to a fragment that was never part of a document load. What actually brings the
 * card into view is `focusSettingAnchor`'s `focus()` call
 * (`lib/setting-jump.ts`), which scrolls by default — the fragment's job is to
 * make the URL addressable, so the link can be copied, bookmarked and pasted.
 *
 * Passing a `controlId` addresses one option inside a card that holds several
 * unrelated ones (Account preferences, Limits): the id is derived from the
 * card's, so a control can never be addressed independently of the card it
 * lives in.
 */
export function settingAnchorHref(key: SettingKey, controlId?: string): string {
  const { id, path, pane } = SETTING_ANCHORS[key];
  const target = controlId === undefined ? id : `${id}--${controlId}`;
  // `paneParamFor` cannot miss for a registry-built anchor — `anchor()` refuses a
  // `pane` on a path the route table does not list — so the fallback below is
  // unreachable rather than a silent no-query, which is the failure it replaces.
  const param = pane === undefined ? undefined : paneParamFor(path);
  const query = param === undefined || pane === undefined ? '' : `?${param}=${pane}`;
  return `${path}${query}#${target}`;
}
